import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import type { Ref } from 'vue';
import { Form, Spin, notification } from 'ant-design-vue';
import router from '@/router';
import i18n from '@/i18n';
import CMSUserRepository from '@/repositories/CMSUserRepository';
import CMSUser from '@/models/CMSUser';
import Schedule from '@/models/Schedule';
import Resource from '@/models/Resource';
import ResourceRepository from '@/repositories/ResourceRepository';
import moment from 'moment';
import RecurrenceRule from '@/modules/CMS/components/RecurrenceRule.vue';
import { RouteNames } from '@/enums/routes/RouteNames';
import ScheduleRepository from '@/repositories/ScheduleRepository';
import RuleRepository from '@/repositories/RuleRepository';
import Rule from '@/models/Rule';
import type Rrule from '@/models/interfaces/Rrule';


export default /*#__PURE__*/_defineComponent({
  __name: 'CalendarScheduleEdit',
  setup(__props) {

const FormItem = Form.Item;

const id = ref<string | null>(null);
const type = ref('user');
const itemData = ref<Schedule>({} as Schedule);
const rrule = ref({} as Rrule) as Ref<Rrule>;
const rruleStr = ref('');
const isLoading = ref(false);

const resources = computed(() => ResourceRepository.getAll());
const users = computed(() => CMSUserRepository.getAll());

async function getCalendarScheduleData() {
    try {
        if (!id.value) {
            return;
        }
        await Schedule.getById(id.value);
        itemData.value = ScheduleRepository.getById(id.value) as Schedule;

        await Rule.getById(itemData.value.ruleId);

        // @ts-ignore
        rrule.value = RuleRepository.getById(itemData.value.ruleId) as Rrule;

        if (rrule.value) {
            rrule.value.startDateTime = formatFromNumberDateToMoment(itemData.value.dateTimeFrom);
            rrule.value.endDateTime = formatFromNumberDateToMoment(itemData.value.dateTimeTo);

            rruleStr.value = itemData.value.recurrenceExpression ? itemData.value.recurrenceExpression : rruleStr.value;
        }
        isLoading.value = false;
    } catch (e) {
        isLoading.value = false;
        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: (e as Error).message,
        });
    }
}

function formatFromNumberDateToMoment(date: number) {
    const formattedDate = new Date(Number(date));

    return moment(formattedDate);
}

async function addCalendarSchedule() {
    if (!rrule.value || !itemData.value) {
        return;
    }

    const dateTimeFrom = moment(rrule.value.startDateTime);
    const dateTimeTo = moment(rrule.value.endDateTime);

    const duration = moment.duration(dateTimeTo.diff(dateTimeFrom));
    const durationInMs = duration.asMilliseconds();

    try {
        // TO DO: We need to post rrule on separate endpoint
        // @ts-ignore
        const newRule = (await Rule.createNew(rrule.value)) as AxiosResponse;

        if (newRule.status === 200) {
            itemData.value.ruleId = newRule.data.id;
        }

        const data = {
            ...itemData.value,
            dateTimeTo: dateTimeTo.valueOf(),
            dateTimeFrom: dateTimeFrom.valueOf(),
            recurrenceExpression: rruleStr.value,
            duration: durationInMs,
        } as any;

        await Schedule.createNew(data);

        notification.success({
            message: i18n.t('Promjene uspješne!') as string,
            description: '',
        });
        await router.push({
            name: RouteNames.schedulesResources,
            params: { dynamic: 'CalendarSchedules' },
        });
    } catch (e) {
        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: (e as Error).message,
        });
    }
}

async function updateCalendarSchedule() {
    if (id.value && rrule.value && itemData.value) {
        try {
            const dateTimeFrom = moment(rrule.value.startDateTime);
            const dateTimeTo = moment(rrule.value.endDateTime);

            const duration = moment.duration(dateTimeTo.diff(dateTimeFrom));
            const durationInMs = duration.asMilliseconds();

            await Rule.updateExisting(String(rrule.value.id), rrule.value);

            const data = {
                ...itemData.value,
                dateTimeTo: dateTimeTo.valueOf(),
                dateTimeFrom: dateTimeFrom.valueOf(),
                recurrenceExpression: rruleStr.value,
                duration: durationInMs,
            } as any;

            await Schedule.updateExisting(id.value, data);
            notification.success({
                message: i18n.t('Promjene uspješne!') as string,
                description: '',
            });
            await router.push({
                name: RouteNames.schedulesResources,
                params: { dynamic: 'CalendarSchedules' },
            });
        } catch (e) {
            notification.error({
                message: i18n.t('Dogodila se greška') as string,
                description: (e as Error).message,
            });
        }
    }
}

function goBack() {
    router.go(-1);
}

onMounted(async () => {
    id.value = router.currentRoute.params.id ?? null;
    type.value = (router.currentRoute.query.type as string | null) ?? 'user';

    try {
        isLoading.value = true;
        await Promise.all([CMSUser.getAll(), Resource.getAll()]);
    } catch (e) {
        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: (e as Error).message,
        });
    } finally {
        isLoading.value = false;
    }

    if (router.currentRoute.query.userId) {
        // @ts-ignore
        itemData.value.userId = Number(router.currentRoute.query.userId);
    }

    if (router.currentRoute.query.resourceId) {
        itemData.value.resourceId = router.currentRoute.query.resourceId as string;
    }

    if (id.value) {
        await getCalendarScheduleData();
    }
});

return { __sfc: true,FormItem, id, type, itemData, rrule, rruleStr, isLoading, resources, users, getCalendarScheduleData, formatFromNumberDateToMoment, addCalendarSchedule, updateCalendarSchedule, goBack, Spin, RecurrenceRule }
}

})